// src/components/AlbumDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_BASE } from '../api';

const AlbumDetail = () => {
  const { slug } = useParams();
  const [album, setAlbum] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchAlbum = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/gallery/${slug}`);
        const data = await res.json();
        if (data.success) {
          setAlbum(data.album);
          setMedia(data.media);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error fetching album:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchAlbum();
  }, [slug]);

  if (loading) {
    return (
      <main className="bg-white min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading album...</p>
      </main>
    );
  }

  if (error || !album) {
    return (
      <main className="bg-white min-h-screen">
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold text-primary mb-4">Album Not Found</h1>
            <p className="text-gray-600 mb-6">The album you're looking for doesn't exist or has been removed.</p>
            <Link to="/gallery" className="text-secondary hover:underline font-medium">
              &larr; Back to Gallery
            </Link>
          </div>
        </section>
      </main>
    );
  }

  const images = media.filter(m => m.mediaType === 'image');
  const videos = media.filter(m => m.mediaType === 'video');

  return (
    <main className="bg-white">
      {/* Banner */}
      <section
        className="relative bg-center bg-cover h-72 flex items-center justify-center"
        style={{ backgroundImage: "url('/images/gallery_banner.jpg')" }}
      >
        <div className="bg-black bg-opacity-40 absolute inset-0" />
        <div className="relative z-10 text-center text-white">
          <h1 className="text-4xl font-bold">Our Photos</h1>
        </div>
      </section>

      {/* Title */}
      <section className="py-8">
        <div className="container mx-auto px-4 text-center">
          <Link to="/gallery" className="text-secondary hover:underline text-sm">
            &larr; Back to Gallery
          </Link>
          <h1 className="text-3xl font-bold mt-2 text-primary">{album.title}</h1>
          {album.description && (
            <p className="text-gray-600 mt-2">{album.description}</p>
          )}
        </div>
      </section>

      {/* Images Grid */}
      {images.length > 0 && (
        <section className="container mx-auto px-4 pb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {images.map((item) => (
              <div className="relative group" key={item._id}>
                <img
                  src={`${API_BASE}/${item.path}`}
                  alt={item.caption || item.originalName}
                  className="w-full object-cover"
                />
                <a
                  href={`${API_BASE}/${item.path}`}
                  target="_blank"
                  rel="noreferrer"
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-50 transition"
                >
                  <i className="fa fa-search-plus text-white text-2xl opacity-0 group-hover:opacity-100 transition" />
                </a>
                {item.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-2 opacity-0 group-hover:opacity-100 transition">
                    {item.caption}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Videos Section */}
      {videos.length > 0 && (
        <section className="container mx-auto px-4 pb-16">
          <div className="text-center mb-4">
            <h2 className="text-2xl font-bold text-primary">Videos</h2>
          </div>
          <div className="flex flex-wrap gap-4">
            {videos.map((item) => (
              <div className="w-full md:w-1/2 lg:w-1/3" key={item._id}>
                <video
                  width="100%"
                  height="300"
                  controls
                  src={`${API_BASE}/${item.path}`}
                >
                  Your browser does not support the video tag.
                </video>
                {item.caption && (
                  <p className="text-sm text-gray-600 mt-1">{item.caption}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Empty state */}
      {images.length === 0 && videos.length === 0 && (
        <section className="container mx-auto px-4 pb-16">
          <p className="text-center text-gray-500 py-8">
            No photos or videos in this album yet.
          </p>
        </section>
      )}
    </main>
  );
};

export default AlbumDetail;
